import { useAuth } from "@repo/auth";
import { Button, Skeleton, Textarea } from "@repo/components";
import { EmptyState } from "@repo/blocks";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ChangeEvent, useState } from "react";
import { PageScaffold } from "../../shell/PageScaffold";
import { useVenue } from "../../shell/VenueContext";
import { canAccessVenue } from "../../shell/roles";
import {
  guestsApi,
  guestsKeys,
  validateBlacklistDraft,
  type BlacklistEntry,
  type BlacklistMatcherKind,
  type BlacklistMode,
  type CreateBlacklistRequest,
} from "./guestsApi";

// Shared card + table styling, matching the polished sibling pages (waitlist / guest directory).
const card = css({
  borderWidth: "1px",
  borderColor: "border.default",
  rounded: "lg",
  bg: "bg.default",
  overflow: "hidden",
});
const cardHead = css({
  display: "flex",
  alignItems: "center",
  gap: "3",
  px: "4",
  py: "3",
  borderBottomWidth: "1px",
  borderColor: "border.subtle",
  bg: "bg.subtle",
});
const cardTitle = css({ fontSize: "md", fontWeight: "semibold" });
const control = css({
  minH: "44px",
  px: "3",
  borderWidth: "1px",
  borderColor: "border.default",
  rounded: "md",
  bg: "bg.default",
  fontSize: "sm",
  _focusVisible: { outlineWidth: "2px", outlineStyle: "solid", outlineColor: "border.emphasized" },
});
const fieldLabel = css({
  display: "flex",
  flexDirection: "column",
  gap: "1",
  fontSize: "sm",
  fontWeight: "medium",
  color: "fg.default",
  minW: "40",
});
const scroll = css({ overflowX: "auto", width: "full" });
const table = css({ width: "full", borderCollapse: "collapse", fontSize: "sm" });
const th = css({
  textAlign: "left",
  px: "4",
  py: "2.5",
  fontWeight: "medium",
  color: "fg.muted",
  borderBottomWidth: "1px",
  borderColor: "border.subtle",
  whiteSpace: "nowrap",
});
const td = css({
  px: "4",
  py: "3",
  verticalAlign: "middle",
  borderBottomWidth: "1px",
  borderColor: "border.subtle",
});

// Token-driven badges (no inline hex), meaning carried by label + a dot (RGAA/WCAG).
const badgeBase = css({
  display: "inline-flex",
  alignItems: "center",
  gap: "1",
  px: "2",
  py: "0.5",
  rounded: "md",
  borderWidth: "1px",
  fontSize: "2xs",
  fontWeight: "medium",
  whiteSpace: "nowrap",
});
const modeBlockClass = css({
  bg: "booking.noShow.bg",
  color: "booking.noShow.fg",
  borderColor: "booking.noShow.border",
});
const modeFlagClass = css({
  bg: "booking.pending.bg",
  color: "booking.pending.fg",
  borderColor: "booking.pending.border",
});
const stateBadgeClass: Record<BlacklistEntry["state"], string> = {
  ACTIVE: css({
    bg: "booking.confirmed.bg",
    color: "booking.confirmed.fg",
    borderColor: "booking.confirmed.border",
  }),
  LAPSED: css({ bg: "bg.muted", color: "fg.muted", borderColor: "border.default" }),
  REMOVED: css({
    bg: "booking.cancelled.bg",
    color: "booking.cancelled.fg",
    borderColor: "booking.cancelled.border",
  }),
};

/**
 * The `/v/:venueId/blacklist` manager surface (features/guest-crm-blacklist.md, §C.8, R2/R5/R6/R7).
 * MANAGER-gated: create (mandatory reason, R2), remove behind an inline confirm (an open appeal
 * auto-resolves, R7), and the review-to-renew queue (R6). NAME_ONLY defaults to FLAG in the form (a
 * common name never hard-blocks, R3) with an explicit "escalate to block" toggle. The server re-enforces
 * R5; the diner never sees any of this (GU-4).
 */
export function BlacklistPage() {
  const { venueId } = useVenue();
  const { claims } = useAuth();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canManage = canAccessVenue(claims, venueId, "MANAGER");

  const entries = useQuery({
    queryKey: guestsKeys.blacklist(venueId),
    queryFn: () => guestsApi.listBlacklist(venueId, true),
    enabled: canManage,
  });
  const review = useQuery({
    queryKey: guestsKeys.review(venueId),
    queryFn: () => guestsApi.reviewQueue(venueId),
    enabled: canManage,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: guestsKeys.blacklist(venueId) });
    void qc.invalidateQueries({ queryKey: guestsKeys.review(venueId) });
  };

  const create = useMutation({
    mutationFn: (body: CreateBlacklistRequest) => guestsApi.createBlacklist(venueId, body),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (entryId: string) => guestsApi.removeBlacklist(venueId, entryId),
    onSuccess: invalidate,
  });
  const renew = useMutation({
    mutationFn: (v: { entryId: string; reason: string }) =>
      guestsApi.renewBlacklist(venueId, v.entryId, v.reason),
    onSuccess: invalidate,
  });

  if (!canManage) {
    return (
      <PageScaffold title={t("guests.blacklist.title")}>
        <p role="alert" className={css({ color: "fg.error", fontSize: "sm" })}>
          {t("guests.blacklist.managerOnly")}
        </p>
      </PageScaffold>
    );
  }

  const rows = entries.data ?? [];

  return (
    <PageScaffold title={t("guests.blacklist.title")}>
      <CreateForm
        onSubmit={(b) => create.mutate(b)}
        pending={create.isPending}
        error={create.isError}
      />

      {review.data && review.data.length > 0 && (
        <section className={card} aria-labelledby="bl-review">
          <div className={cardHead}>
            <h2 id="bl-review" className={cardTitle}>
              {t("guests.blacklist.reviewTitle")}
            </h2>
          </div>
          <ul className={css({ listStyle: "none", p: "0", m: "0" })}>
            {review.data.map((e) => (
              <ReviewRow
                key={e.entryId}
                entry={e}
                onRenew={(reason) => renew.mutate({ entryId: e.entryId, reason })}
                pending={renew.isPending}
                t={t}
              />
            ))}
          </ul>
        </section>
      )}

      <section className={card} aria-labelledby="bl-entries">
        <div className={cardHead}>
          <h2 id="bl-entries" className={cardTitle}>
            {t("guests.blacklist.entriesTitle")}
          </h2>
        </div>

        {entries.isPending && (
          <div className={css({ p: "4" })} data-testid="blacklist-loading">
            <Skeleton height="8" mb="3" />
            <Skeleton height="6" mb="2" />
            <Skeleton height="6" />
          </div>
        )}
        {entries.isError && (
          <div
            role="alert"
            data-testid="blacklist-error"
            className={css({ p: "4", color: "fg.error", fontSize: "sm" })}
          >
            {t("guests.blacklist.loadError")}
          </div>
        )}
        {entries.data && rows.length === 0 && (
          <div data-testid="blacklist-empty">
            <EmptyState icon="🛡" title={t("guests.blacklist.empty")} />
          </div>
        )}

        {rows.length > 0 && (
          <div className={scroll}>
            <table className={table}>
              <thead>
                <tr>
                  <th className={th}>{t("guests.blacklist.colMatcher")}</th>
                  <th className={th}>{t("guests.blacklist.colMode")}</th>
                  <th className={th}>{t("guests.blacklist.colReason")}</th>
                  <th className={th}>{t("guests.blacklist.colState")}</th>
                  <th className={th}>
                    <span className={css({ srOnly: true })}>
                      {t("guests.blacklist.colActions")}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => (
                  <EntryRow
                    key={e.entryId}
                    entry={e}
                    onRemove={() => remove.mutate(e.entryId)}
                    pending={remove.isPending}
                    t={t}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </PageScaffold>
  );
}

type Translate = (key: string, opts?: Record<string, unknown>) => string;

function ModeBadge({ mode, t }: { mode: BlacklistMode; t: Translate }) {
  return (
    <span className={`${badgeBase} ${mode === "BLOCK" ? modeBlockClass : modeFlagClass}`}>
      {t(`guests.blacklist.modeShort.${mode}`)}
    </span>
  );
}

function EntryRow({
  entry,
  onRemove,
  pending,
  t,
}: {
  entry: BlacklistEntry;
  onRemove: () => void;
  pending: boolean;
  t: Translate;
}) {
  const [confirming, setConfirming] = useState(false);
  return (
    <tr data-testid="blacklist-row">
      <td className={td}>
        <div className={css({ fontWeight: "medium" })}>{entry.matcherLabel}</div>
        {entry.state === "ACTIVE" && (
          <div className={css({ fontSize: "xs", color: "fg.muted" })}>
            {t("guests.blacklist.expiresAt", {
              date: new Date(entry.expiresAt).toLocaleDateString(),
            })}
          </div>
        )}
      </td>
      <td className={td}>
        <ModeBadge mode={entry.mode} t={t} />
      </td>
      <td className={td}>{entry.reason}</td>
      <td className={td}>
        <span className={`${badgeBase} ${stateBadgeClass[entry.state]}`}>
          {t(`guests.blacklist.stateValue.${entry.state}`)}
        </span>
      </td>
      <td className={td}>
        {entry.state === "ACTIVE" &&
          (confirming ? (
            <div
              className={css({ display: "flex", gap: "2", flexWrap: "wrap", alignItems: "center" })}
            >
              <span className={css({ fontSize: "sm" })}>
                {t("guests.blacklist.confirm.remove")}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={pending}
                data-testid="blacklist-remove-confirm"
                onClick={() => {
                  setConfirming(false);
                  onRemove();
                }}
              >
                {t("guests.blacklist.confirm.proceed")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
                {t("guests.blacklist.confirm.dismiss")}
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              data-testid="blacklist-remove"
              onClick={() => setConfirming(true)}
            >
              {t("guests.blacklist.remove")}
            </Button>
          ))}
      </td>
    </tr>
  );
}

function ReviewRow({
  entry,
  onRenew,
  pending,
  t,
}: {
  entry: BlacklistEntry;
  onRenew: (reason: string) => void;
  pending: boolean;
  t: Translate;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  return (
    <li
      className={css({
        px: "4",
        py: "3",
        borderBottomWidth: "1px",
        borderColor: "border.subtle",
        display: "flex",
        flexDirection: "column",
        gap: "2",
      })}
    >
      <div className={css({ display: "flex", gap: "3", flexWrap: "wrap", alignItems: "center" })}>
        <span className={css({ fontWeight: "medium", fontSize: "sm" })}>{entry.matcherLabel}</span>
        <span className={css({ fontSize: "xs", color: "fg.muted", flex: "1", minW: "32" })}>
          {t("guests.blacklist.expiresAt", {
            date: new Date(entry.expiresAt).toLocaleDateString(),
          })}
        </span>
        {!open && (
          <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
            {t("guests.blacklist.renew")}
          </Button>
        )}
      </div>
      {open && (
        <form
          className={css({ display: "flex", gap: "2", flexWrap: "wrap", alignItems: "flex-end" })}
          onSubmit={(e) => {
            e.preventDefault();
            if (reason.trim()) onRenew(reason.trim());
          }}
        >
          <label className={`${fieldLabel} ${css({ flex: "1" })}`}>
            <span className={css({ srOnly: true })}>{t("guests.blacklist.renewReason")}</span>
            <input
              className={control}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("guests.blacklist.renewReason")}
              data-testid="blacklist-renew-reason"
            />
          </label>
          <Button
            type="submit"
            size="sm"
            loading={pending}
            disabled={!reason.trim()}
            data-testid="blacklist-renew-submit"
          >
            {t("guests.blacklist.renewSubmit")}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
            {t("guests.blacklist.confirm.dismiss")}
          </Button>
        </form>
      )}
    </li>
  );
}

function CreateForm({
  onSubmit,
  pending,
  error,
}: {
  onSubmit: (body: CreateBlacklistRequest) => void;
  pending: boolean;
  error: boolean;
}) {
  const { t } = useTranslation();
  const [matcherType, setMatcherType] = useState<BlacklistMatcherKind>("PHONE");
  const [value, setValue] = useState("");
  const [mode, setMode] = useState<BlacklistMode>("BLOCK");
  const [reason, setReason] = useState("");
  const [escalate, setEscalate] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const draft: CreateBlacklistRequest = {
      matcherType,
      phone: matcherType === "PHONE" ? value : undefined,
      email: matcherType === "EMAIL" ? value : undefined,
      name: matcherType === "NAME_ONLY" ? value : undefined,
      mode: matcherType === "NAME_ONLY" ? undefined : mode,
      reason,
      escalateNameToBlock: matcherType === "NAME_ONLY" ? escalate : undefined,
    };
    const invalid = validateBlacklistDraft(draft);
    if (invalid) {
      setLocalError(invalid);
      return;
    }
    setLocalError(null);
    onSubmit(draft);
    setValue("");
    setReason("");
  };

  return (
    <section className={card} aria-labelledby="bl-create">
      <div className={cardHead}>
        <h2 id="bl-create" className={cardTitle}>
          {t("guests.blacklist.createTitle")}
        </h2>
      </div>
      <form
        onSubmit={submit}
        className={css({ display: "flex", flexDirection: "column", gap: "3", p: "4" })}
      >
        <div
          className={css({ display: "flex", gap: "3", flexWrap: "wrap", alignItems: "flex-end" })}
        >
          <label className={fieldLabel}>
            {t("guests.blacklist.matcherType")}
            <select
              className={control}
              value={matcherType}
              onChange={(e) => setMatcherType(e.target.value as BlacklistMatcherKind)}
            >
              <option value="PHONE">{t("guests.blacklist.matcher.PHONE")}</option>
              <option value="EMAIL">{t("guests.blacklist.matcher.EMAIL")}</option>
              <option value="NAME_ONLY">{t("guests.blacklist.matcher.NAME_ONLY")}</option>
            </select>
          </label>
          <label className={`${fieldLabel} ${css({ flex: "1" })}`}>
            {t("guests.blacklist.matcherValue")}
            <input
              className={control}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={t(`guests.blacklist.placeholder.${matcherType}`)}
              data-testid="blacklist-value"
            />
          </label>
          {matcherType !== "NAME_ONLY" ? (
            <label className={fieldLabel}>
              {t("guests.blacklist.matcherMode")}
              <select
                className={control}
                value={mode}
                onChange={(e) => setMode(e.target.value as BlacklistMode)}
              >
                <option value="BLOCK">{t("guests.blacklist.mode.BLOCK")}</option>
                <option value="FLAG">{t("guests.blacklist.mode.FLAG")}</option>
              </select>
            </label>
          ) : (
            <label
              className={css({
                display: "flex",
                gap: "2",
                alignItems: "center",
                minH: "44px",
                fontSize: "sm",
              })}
            >
              <input
                type="checkbox"
                checked={escalate}
                onChange={(e) => setEscalate(e.target.checked)}
              />
              {t("guests.blacklist.escalateNameToBlock")}
            </label>
          )}
        </div>

        <label className={fieldLabel}>
          {t("guests.blacklist.reason")}
          <Textarea
            value={reason}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setReason(e.target.value)}
            placeholder={t("guests.blacklist.reasonPlaceholder")}
            rows={2}
            data-testid="blacklist-reason"
          />
        </label>

        {localError && (
          <p role="alert" className={css({ color: "fg.error", fontSize: "sm" })}>
            {t(`guests.blacklist.error.${localError}`)}
          </p>
        )}
        {error && (
          <p role="alert" className={css({ color: "fg.error", fontSize: "sm" })}>
            {t("guests.blacklist.createError")}
          </p>
        )}
        <Button
          type="submit"
          size="sm"
          alignSelf="flex-start"
          loading={pending}
          data-testid="blacklist-create-submit"
        >
          {t("guests.blacklist.createSubmit")}
        </Button>
      </form>
    </section>
  );
}
